import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useState } from "react";
import { MultiValue, SingleValue } from "react-select";
import InfoDialog from "./info-dialog";
import StyledSelect from "../../components/styled-select";
import { OptionValue } from "../../types/component";
import { isSelectSingleValue } from "../../core/utils";
import { AppDispatch, AppState } from "../../redux/store";
import {
  getAttendanceState,
  getAttendanceStateAction,
  GetDataApplicationRequest,
} from "../../redux/features/misa-slice";
import {
  CommonStatus,
  getGroupTeamState,
  GroupTeamResponse,
  groupTeamStateAction,
} from "../../redux/features/other-slice";
import FullCalendarComponent from "./components/full-calendar";
import { getUserState } from "../../redux/features/auth-slice";
import {
  allUsersStateAction,
  UserResponse,
} from "../../redux/features/user-slice";
import StyledLabel from "../../components/styled-label";
import { setBreadcrumbState } from "../../redux/features/common-slice";
const types = ["Attendance", "OverTime"];
export default function WorkCalendar() {
  const user = useSelector(getUserState).data;
  const dispatch = useDispatch<AppDispatch>();
  const attendanceState = useSelector((state: AppState) =>
    getAttendanceState(state)
  );
  const groupTeamState = useSelector(getGroupTeamState);
  const [appType, setAppType] = useState(types[0]);
  const [selectedTeams, setSelectedTeams] = useState<GroupTeamResponse[]>([]);
  const [applications, setApplications] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [app, setApp] = useState<{ data: any; user: UserResponse | null } | null>(null);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>();
  const [users, setUsers] = useState<UserResponse[]>([]);
  const isRestricted = user?.email === "JordanG@tilling.com.au";
  const allowedEmails = [
    "thu.nguyen@myteamsolution.com.au",
    "hoc.nguyen@myteamsolution.com.au",
    "ngan.tran@myteamsolution.com.au",
    "hoa.nguyen@myteamsolution.com.au",
    "ha.nguyen@myteamsolution.com.au",
    "hung.nguyen@myteamsolution.com.au",
    "uyen.le@myteamsolution.com.au",
    "thanh.doan@myteamsolution.com.au",
    "quyn.do@myteamsolution.com.au",
    "trang.dang@myteamsolution.com.au",
    "khanh.trinh@myteamsolution.com.au",
    "vinh.pham@myteamsolution.com.vn",
    "toan.pham@myteamsolution.com.vn",
    "tinh.tran@myteamsolution.com.vn",
    "vu.pham@myteamsolution.com.vn",
    "mai.chau@myteamsolution.com.vn",
    "khanh.nguyen@myteamsolution.com.vn",
  ].map((e) => e.toLowerCase());
  const fetchData = useCallback(async () => {
    try {
      await dispatch(
        groupTeamStateAction({
          status: [CommonStatus.Active],
        })
      );
      const response = await dispatch(
        allUsersStateAction({
          teamIds: [],
          roles: [],
        })
      ).unwrap();
      if (isRestricted) {
        setUsers(
          response.filter((u: UserResponse) =>
            allowedEmails.includes(u.email?.toLowerCase() || "")
          )
        );
      } else {
        setUsers(response);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [dispatch, isRestricted]);
  const fetchAttendance = useCallback(async () => {
    if (dateRange) {
      try {
        const params: GetDataApplicationRequest = {
          Filter: null,
          CustomFilter: null,
          QuickSearch: {},
          CustomParam: {
            SubSystemCode: appType,
            FromDate: dateRange.start,
            ToDate: dateRange.end,
          },
        };
        await dispatch(getAttendanceStateAction(params));
      } catch (error) {
        console.error("Error fetching attendance:", error);
      }
    }
  }, [dispatch, dateRange, appType]);
  useEffect(() => {
    dispatch(
      setBreadcrumbState([
        { id: "work-calendar", path: null, title: "Work Calendar" },
      ])
    );
  }, [dispatch]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);
  useEffect(() => {
    if (attendanceState.data?.Data?.PageData) {
      let datas = attendanceState.data.Data.PageData;
      datas = datas.filter((e: any) => e.Status === 2);
      const filterUsers =
        selectedTeams.length > 0
          ? users.filter((e: UserResponse) =>
              selectedTeams.some(
                (t: GroupTeamResponse) => t.id === e.groupTeam?.id
              )
            )
          : users;
      datas = datas.filter((e: any) =>
        filterUsers.some((u: UserResponse) => u.misaCode === e.EmployeeCode)
      );
      setApplications(datas);
      const tmpAtt = [];
      for (const atts of datas) {
        if (appType === "Attendance") {
          const leaveDay = parseFloat(atts.LeaveDay?.toString() || "0");
          tmpAtt.push({
            id: atts[`${appType}ID`],
            title: `${atts.FullName} - ${leaveDay} ${
              leaveDay > 1 ? "days" : "day"
            }`,
            start: atts.FromDate,
            end: atts.ToDate,
            RequestDate: atts.RequestDate,
            backgroundColor:
              leaveDay >= 3 ? "#FFB400" : leaveDay > 1 ? "#16B1FF" : "#56CA00",
            borderColor:
              leaveDay >= 3 ? "#E09E00" : leaveDay > 1 ? "#139CE0" : "#4CB200",
            display: "backgroundColor",
          });
        } else {
          tmpAtt.push({
            id: (atts as any)[`${appType}ID`],
            title: atts.FullName,
            start: atts.FromDate,
            end: atts.ToDate,
            RequestDate: atts.RequestDate,
            display: "backgroundColor",
          });
        }
      }
      setEvents(tmpAtt);
    }
  }, [attendanceState.data, appType, selectedTeams, users]);
  return (
    <div className="h-full flex flex-col overflow-y-auto">
      <div className="w-full flex items-center space-x-4 mb-4">
        <div>
          <StyledLabel label="Type" />
          <StyledSelect
            options={types.map((type: string) => ({
              value: type,
              label: type,
            }))}
            isClearable={false}
            value={{
              value: appType,
              label: appType,
            }}
            onChange={(
              value: SingleValue<OptionValue> | MultiValue<OptionValue>
            ) => {
              if (value && isSelectSingleValue(value)) {
                setAppType(value.value as string);
              }
            }}
            exClassName="z-10"
          />
        </div>
        {!isRestricted && (
          <div>
            <StyledLabel label="Team" />
            <StyledSelect
              options={
                groupTeamState.data?.map((groupTeam: GroupTeamResponse) => ({
                  value: groupTeam.id,
                  label: groupTeam.name,
                })) || []
              }
              value={{
                value: "",
                label: `Team: ${selectedTeams.length} selected`,
              }}
              loading={groupTeamState.loading}
              isClearable={false}
              onChange={(
                value: SingleValue<OptionValue> | MultiValue<OptionValue>
              ) => {
                if (value && isSelectSingleValue(value)) {
                  if (
                    selectedTeams.findIndex((e) => e.id === value.value) !== -1
                  ) {
                    setSelectedTeams(
                      selectedTeams.filter((e) => e.id !== value.value)
                    );
                  } else {
                    setSelectedTeams([
                      ...selectedTeams,
                      {
                        id: value.value as string,
                        name: value.label,
                        status: CommonStatus.Active,
                      },
                    ]);
                  }
                }
              }}
              isCloseMenuOnSelect={false}
              exClassName="w-full z-10"
              exOption={(value: OptionValue) => {
                return {
                  background:
                    selectedTeams.findIndex((e) => e.id === value.value) !== -1
                      ? "#f1f5f9"
                      : undefined,
                };
              }}
            />
          </div>
        )}
      </div>
      <FullCalendarComponent
        data={events}
        onMonthChange={(start: string, end: string) => {
          setDateRange({ start, end });
        }}
        onDetail={(id: any) => {
          const index = applications.findIndex(
            (e) => e[`${appType}ID`] === parseInt(id)
          );
          if (index !== -1) {
            const selectedApp = applications[index];
            const selectedUser = users.find(
              (u) => u.misaCode === selectedApp.EmployeeCode
            ) || null;
            setApp({ data: selectedApp, user: selectedUser });
          }
        }}
      />
      <InfoDialog
        type={appType}
        data={app?.data}
        setData={() => setApp(null)}
        hideReason={true}
        user={app?.user ?? null}
      />
    </div>
  );
}